import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Section } from '@astryxdesign/core/Section';

/**
 * Fades a section in when it enters the viewport. Content is rendered (and
 * visible to screen readers) from the start; with reduced motion the CSS in
 * globals.css shows it immediately.
 */
export function Reveal({ children, label }: { children: ReactNode; label?: string }) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className="reveal" data-visible={visible} aria-label={label}>
      <Section variant="transparent" padding={0}>
        {children}
      </Section>
    </section>
  );
}
